import React from 'react';
import CreativeHero from '../components/HeroSlider';
import ServicesGrid from '../components/ServicesGrid';
import BlogGrid from '../components/BlogGrid';
import TabbedPricingSection from '../components/TabbedPricingSection';
import TestimonialsSection from '../components/TestimonialsSection';
import ServiceContactForm from '../components/ServiceContactForm';
import { motion } from 'framer-motion';
import { ArrowRight, Zap, Users, Award, TrendingUp, Rocket, Shield, Clock } from 'lucide-react';
import { Link } from 'react-router-dom';
import { stats } from '../mockData';

const Home = () => {
  return (
    <div className="bg-white">
      {/* Creative Hero */}
      <CreativeHero />

      {/* Stats Section - More Creative */}
      <section className="py-16 px-4 lg:px-8 bg-gradient-to-br from-black via-dark-grey to-black text-white relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0" style={{
            backgroundImage: 'radial-gradient(circle at 2px 2px, rgba(255,255,255,0.15) 1px, transparent 0)',
            backgroundSize: '40px 40px'
          }} />
        </div>
        
        <div className="max-w-8xl mx-auto relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-20"
          >
            <h2 className="text-5xl md:text-6xl font-black mb-4">
              <span className="bg-gradient-to-r from-light-pink via-mid-purple to-light-blue bg-clip-text text-transparent">
                Proven Excellence
              </span>
            </h2>
            <p className="text-xl text-grey">Numbers that speak for themselves</p>
          </motion.div>

          <div className="grid grid-cols-2 lg:grid-cols-4 gap-8 lg:gap-12">
            {stats.map((stat, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30, scale: 0.9 }}
                whileInView={{ opacity: 1, y: 0, scale: 1 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                whileHover={{ scale: 1.05, y: -5 }}
                className="relative group"
              >
                <div className="bg-gradient-to-br from-white/5 to-white/10 backdrop-blur-sm rounded-3xl p-8 border border-white/10 hover:border-light-pink/50 transition-all duration-300">
                  <div className="absolute top-4 right-4 w-12 h-12 rounded-full bg-light-pink/20 flex items-center justify-center">
                    {index === 0 && <Zap size={24} className="text-light-pink" />}
                    {index === 1 && <Users size={24} className="text-mid-purple" />}
                    {index === 2 && <Award size={24} className="text-mid-blue" />}
                    {index === 3 && <TrendingUp size={24} className="text-light-yellow" />}
                  </div>
                  <div className="text-6xl md:text-7xl font-black mb-3 bg-gradient-to-br from-light-pink to-mid-purple bg-clip-text text-transparent">
                    {stat.value}
                  </div>
                  <div className="text-sm md:text-base text-grey font-mono uppercase tracking-wider">{stat.label}</div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* About Preview - More Creative Layout */}
      <section className="py-20 px-4 lg:px-8 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-light-pink/20 via-mid-purple/20 to-light-blue/20" />
        
        <div className="max-w-7xl mx-auto relative z-10">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
            >
              <div className="mb-6">
                <span className="text-mid-purple text-6xl">✦</span>
              </div>
              <h2 className="text-5xl md:text-6xl lg:text-7xl font-black mb-6 leading-tight">
                Who We Are
              </h2>
              <div className="w-24 h-2 bg-gradient-to-r from-light-pink to-mid-purple rounded-full mb-8" />
              <p className="text-xl md:text-2xl text-dark-grey mb-6 leading-relaxed">
                <strong>SYNCORE LLC</strong> is a cutting-edge digital agency where <strong className="text-black">innovation meets execution</strong>.
              </p>
              <p className="text-lg text-dark-grey mb-8 leading-relaxed">
                We combine creativity with technical expertise to deliver exceptional results. From web development to blockchain solutions, we're your trusted partner in digital transformation.
              </p>
              <Link to="/about">
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  className="px-8 py-4 rounded-full bg-black text-white font-mono text-sm uppercase tracking-wider font-bold hover:bg-mid-purple transition-colors inline-flex items-center gap-3 group"
                >
                  Learn More About Us
                  <ArrowRight size={20} className="group-hover:translate-x-1 transition-transform" />
                </motion.button>
              </Link>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
              className="relative"
            >
              <div className="relative rounded-3xl overflow-hidden">
                <img 
                  src="https://images.unsplash.com/photo-1522071820081-009f0129c71c?w=800&h=600&fit=crop" 
                  alt="Team collaboration"
                  className="w-full h-[500px] object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-br from-mid-purple/30 to-transparent" />
              </div>
              {/* Floating card */}
              <motion.div
                className="absolute -bottom-8 -left-8 bg-white rounded-2xl p-6 shadow-2xl border border-grey"
                animate={{ y: [0, -10, 0] }}
                transition={{ duration: 3, repeat: Infinity }}
              >
                <div className="text-4xl font-black text-mid-purple mb-1">15+</div>
                <div className="text-sm text-dark-grey font-mono">Years of Innovation</div>
              </motion.div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <ServicesGrid />

      {/* Why Choose Us Section */}
      <section className="py-32 px-4 lg:px-8 bg-white relative overflow-hidden">
        <div className="max-w-8xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="text-center mb-20"
          >
            <h2 className="text-5xl md:text-6xl lg:text-7xl font-black mb-6">
              <span className="bg-gradient-to-r from-mid-purple via-mid-pink to-light-blue bg-clip-text text-transparent">
                Why Choose Us
              </span>
            </h2>
            <p className="text-xl md:text-2xl text-dark-grey max-w-3xl mx-auto leading-relaxed">
              We deliver excellence through innovation, expertise, and dedication
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              {
                icon: Rocket,
                title: "Fast Delivery",
                description: "We understand the importance of time-to-market. Our agile approach ensures rapid development without compromising quality.",
                color: "#ffd1e7"
              },
              {
                icon: Shield,
                title: "Security First",
                description: "Your data and users are protected with enterprise-grade security measures and best practices built into every project.",
                color: "#d987ff"
              },
              {
                icon: Clock,
                title: "24/7 Support",
                description: "Round-the-clock technical support and maintenance to ensure your digital assets perform optimally at all times.",
                color: "#88a2ff"
              }
            ].map((item, index) => {
              const Icon = item.icon;
              return (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                  whileHover={{ y: -10, scale: 1.02 }}
                  className="relative group"
                >
                  <div 
                    className="rounded-3xl p-8 lg:p-10 border-2 border-grey hover:border-mid-purple transition-all duration-300 h-full"
                    style={{ backgroundColor: `${item.color}20` }}
                  >
                    <motion.div
                      whileHover={{ rotate: [0, -10, 10, 0], scale: 1.1 }}
                      transition={{ duration: 0.5 }}
                      className="w-16 h-16 rounded-2xl mb-6 flex items-center justify-center"
                      style={{ backgroundColor: item.color }}
                    >
                      <Icon size={32} className="text-black" />
                    </motion.div>
                    <h3 className="text-3xl font-black mb-4">{item.title}</h3>
                    <p className="text-base text-dark-grey leading-relaxed">{item.description}</p>
                  </div>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Pricing Section - Tabbed */}
      <TabbedPricingSection />

      {/* Testimonials Section */}
      <TestimonialsSection />

      {/* CTA Section - More Creative */}
      <section className="py-32 px-4 lg:px-8 bg-gradient-to-br from-[#1a0a2e] via-[#2d1b4e] to-[#1a0a2e] relative overflow-hidden">
        <div className="absolute inset-0 opacity-20">
          <motion.div
            animate={{ rotate: 360 }}
            transition={{ duration: 50, repeat: Infinity, ease: "linear" }}
            className="absolute top-0 left-0 w-full h-full"
            style={{
              backgroundImage: 'radial-gradient(circle, rgba(255,255,255,0.3) 2px, transparent 2px)',
              backgroundSize: '50px 50px'
            }}
          />
        </div>
        
        <div className="max-w-5xl mx-auto text-center relative z-10">
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            <motion.div
              animate={{ scale: [1, 1.2, 1] }}
              transition={{ duration: 2, repeat: Infinity }}
              className="inline-block mb-6"
            >
              <span className="text-8xl">✨</span>
            </motion.div>
            <h2 className="text-5xl md:text-6xl lg:text-7xl font-bold mb-6 text-white leading-tight tracking-tight" style={{ fontWeight: 700 }}>
              Ready to Create Something Amazing?
            </h2>
            <p className="text-xl md:text-2xl text-white/90 mb-12 leading-relaxed max-w-3xl mx-auto">
              Let's collaborate and transform your vision into reality. Get in touch with our team today.
            </p>
            <motion.button
              onClick={() => document.getElementById('contact-form')?.scrollIntoView({ behavior: 'smooth' })}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              className="px-12 py-5 rounded-full bg-white text-black font-mono text-sm uppercase tracking-wider font-bold hover:bg-black hover:text-white transition-colors inline-flex items-center gap-3 group shadow-2xl"
            >
              Start Your Project
              <ArrowRight size={24} className="group-hover:translate-x-2 transition-transform" />
            </motion.button>
          </motion.div>
        </div>
      </section>

      {/* Blog Preview */}
      <section className="py-32 px-4 lg:px-8 bg-white">
        <div className="max-w-8xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <h2 className="text-5xl md:text-6xl lg:text-7xl font-black mb-6">
              <span className="bg-gradient-to-r from-mid-purple via-mid-pink to-light-blue bg-clip-text text-transparent">
                Latest Insights
              </span>
            </h2>
            <p className="text-xl md:text-2xl text-dark-grey max-w-3xl mx-auto">
              Tutorials, trends, and thought leadership from our expert team
            </p>
          </motion.div>

          <BlogGrid limit={3} />

          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.3 }}
            className="text-center mt-16"
          >
            <Link to="/blog">
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className="px-10 py-4 rounded-full bg-black text-white font-mono text-sm uppercase tracking-wider font-bold hover:bg-mid-purple transition-colors inline-flex items-center gap-3 group"
              >
                View All Posts
                <ArrowRight size={20} className="group-hover:translate-x-1 transition-transform" />
              </motion.button>
            </Link>
          </motion.div>
        </div>
      </section>

      {/* Contact Form */}
      <ServiceContactForm serviceName="General Inquiry" />
    </div>
  );
};

export default Home;
